/**
 * `CitationChip` — a clickable pill representing a documentation citation
 * on an assistant chat message.
 *
 * Rendered by {@link ./ChatbotDock | ChatbotDock} for every entry in a
 * {@link ChatbotResponse.citedDocs} array. Clicking a chip calls
 * {@link openDocsAt} which fires the `help-docs-panel:open` window event
 * subscribed by `DocsSliderProvider` (Wave 2.1) — the docs slider opens
 * at the cited path while the chatbot dock stays open (Requirement 8.6).
 *
 * ## Contract
 *
 * - Renders a `<button>` — never an `<a>` — so keyboard activation
 *   (Space/Enter) works out of the box without navigating the browser.
 * - The button carries `data-citation-chip` so integration tests can
 *   locate it without depending on the visual text.
 * - The accessible name is `"Open documentation at {path}"` so a screen
 *   reader user hears the intent, not just the path.
 * - `path` is not validated here — callers pass manifest-relative paths
 *   from `citedDocs[]`, and `openDocsAt` re-validates on invocation.
 *
 * @see .kiro/specs/help-docs-panel/requirements.md — Requirement 8.6
 * @see .kiro/specs/help-docs-panel/design.md — Component 7 "CitationChip"
 * @see ./openDocsAt.ts — the imperative helper this chip drives
 *
 * @packageDocumentation
 */
import * as React from "react";
import { openDocsAt } from "./openDocsAt";

/**
 * Props accepted by {@link CitationChip}.
 */
export interface CitationChipProps {
  /**
   * Manifest-relative documentation path, e.g. `"api/statements.md"`.
   *
   * @remarks Validated downstream by {@link openDocsAt} — MUST end in
   *   `.md`, MUST NOT start with `/`, MUST NOT contain `..`.
   */
  path: string;
}

/**
 * Renders a `<button>` chip that opens the docs slider at `path` when
 * clicked or activated via keyboard. The chatbot dock stays open.
 */
export function CitationChip(props: CitationChipProps): JSX.Element {
  const { path } = props;
  return (
    <button
      type="button"
      data-citation-chip=""
      aria-label={`Open documentation at ${path}`}
      onClick={() => openDocsAt(path)}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      <span aria-hidden="true">📄</span>
      <span className="truncate max-w-[220px]">{path}</span>
    </button>
  );
}
